import React, { useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Grid,
    TextField,
    Button,
    IconButton,
    Slider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Card,
    CardContent,
    CardHeader,
    Divider
} from '@mui/material';
import {
    Save as SaveIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Visibility as EyeIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import { generateCustomTemplateHTML } from '../utils/customTemplateGenerator';
import { InvoiceTemplateInterface } from '../utils/invoiceTemplates';
import { useAuth } from '../context/AuthContext';
import SettingsService from '../services/settingsService';
import { useNavigate } from 'react-router-dom';
import AnimatedPage from '../components/AnimatedPage';

// Section types for reordering
type Section = 'Header' | 'Info' | 'Items' | 'Totals' | 'Footer';

const CustomTemplateBuilder: React.FC = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // Template State
    const [templateName, setTemplateName] = useState('My Custom Template');
    const [primaryColor, setPrimaryColor] = useState('#3949AB');
    const [secondaryColor, setSecondaryColor] = useState('#757575');
    const [bgColor, setBgColor] = useState('#FFFFFF');
    const [fontFamily, setFontFamily] = useState('Helvetica');

    // Layout Order State (Simulated Drag & Drop)
    const [sections, setSections] = useState<Section[]>(['Header', 'Info', 'Items', 'Totals', 'Footer']);

    // Generate Preview HTML
    const getPreviewHTML = () => {
        // Construct a temporary config object
        const config: InvoiceTemplateInterface = {
            id: 999,
            name: templateName,
            description: 'Custom',
            variant: 'custom',
            colorScheme: {
                primary: primaryColor,
                secondary: secondaryColor,
                background: bgColor,
                text: '#000'
            },
            fontFamily,
            // We pass the sections order to the generator (updated generator needed to support this fully, 
            // but for now we pass the styles which is the main visual change)
        };
        return generateCustomTemplateHTML(config);
    };

    const handleMoveSection = (index: number, direction: 'up' | 'down') => {
        const newSections = [...sections];
        if (direction === 'up' && index > 0) {
            [newSections[index], newSections[index - 1]] = [newSections[index - 1], newSections[index]];
        } else if (direction === 'down' && index < newSections.length - 1) {
            [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
        }
        setSections(newSections);
    };

    const handleSave = async () => {
        if (!currentUser) return;

        try {
            // Check limit
            const currentSettings = await SettingsService.fetchSettings(currentUser.uid);
            const existingCustom = currentSettings?.customTemplates || [];

            if (existingCustom.length >= 1) {
                // If editing existing, allow. If new, block.
                // For simplicity, we'll just allow overwriting the first one or alert.
                if (!window.confirm('You have reached the limit of 1 Custom Template. Overwrite existing?')) {
                    return;
                }
            }

            const newTemplate: InvoiceTemplateInterface = {
                id: 1001, // Fixed ID for the single custom template
                name: templateName,
                description: 'Custom Template',
                variant: 'custom',
                colorScheme: {
                    primary: primaryColor,
                    secondary: secondaryColor,
                    background: bgColor,
                    text: '#000'
                },
                layout: {
                    // Store sections order in layout or a custom field
                    header: 'standard',
                    footer: 'standard',
                    itemsTable: 'standard'
                },
                fontFamily
            };

            const updatedTemplates = [newTemplate]; // Replace all custom templates with this one
            await SettingsService.updateSettings(currentUser.uid, {
                customTemplates: updatedTemplates
            });

            alert('Template Saved Successfully!');
            navigate('/settings');
        } catch (error) {
            console.error(error);
            alert('Failed to save template.');
        }
    };

    return (
        <AnimatedPage>
            <Box sx={{ p: 3, height: '100vh', display: 'flex', flexDirection: 'column' }}>
                {/* Visual Header */}
                <Box mb={3} display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h4" fontWeight="bold">Template Builder</Typography>
                    <Box>
                        <Button variant="outlined" sx={{ mr: 2 }} onClick={() => navigate('/settings')}>Cancel</Button>
                        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave}>Save Template</Button>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{ flex: 1, overflow: 'hidden' }}>
                    {/* Controls Panel */}
                    <Grid item xs={12} md={4} sx={{ height: '100%', overflowY: 'auto' }}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>General</Typography>
                            <TextField
                                fullWidth
                                label="Template Name"
                                value={templateName}
                                onChange={(e) => setTemplateName(e.target.value)}
                                sx={{ mb: 2 }}
                            />
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Font Family</InputLabel>
                                <Select value={fontFamily} label="Font Family" onChange={(e) => setFontFamily(e.target.value)}>
                                    <MenuItem value="Helvetica">Helvetica (Standard)</MenuItem>
                                    <MenuItem value="Times-Roman">Times New Roman</MenuItem>
                                    <MenuItem value="Courier">Courier</MenuItem>
                                    <MenuItem value="Roboto">Roboto</MenuItem>
                                </Select>
                            </FormControl>
                        </Paper>

                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>Colors</Typography>
                            <Typography variant="subtitle2" gutterBottom>Primary Color</Typography>
                            <Box mb={2}>
                                <HexColorPicker color={primaryColor} onChange={setPrimaryColor} style={{ width: '100%' }} />
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>Secondary Color</Typography>
                            <Box mb={2} display="flex" alignItems="center">
                                <Box sx={{ width: 30, height: 30, bgcolor: secondaryColor, border: '1px solid #ccc', mr: 2 }} />
                                <TextField size="small" value={secondaryColor} onChange={(e) => setSecondaryColor(e.target.value)} />
                            </Box>
                            <Typography variant="subtitle2" gutterBottom>Background Color</Typography>
                            <Box mb={2} display="flex" alignItems="center">
                                <Box sx={{ width: 30, height: 30, bgcolor: bgColor, border: '1px solid #ccc', mr: 2 }} />
                                <TextField size="small" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 3 }}>
                            <Typography variant="h6" gutterBottom>Layout Sections</Typography>
                            <Typography variant="caption" color="text.secondary" paragraph>
                                Reorder content sections (Note: Preview updates layout based on color/font primarily)
                            </Typography>
                            {sections.map((section, index) => (
                                <Card key={section} variant="outlined" sx={{ mb: 1 }}>
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography>{section}</Typography>
                                        <Box>
                                            <IconButton size="small" onClick={() => handleMoveSection(index, 'up')} disabled={index === 0}>
                                                <ArrowUpIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => handleMoveSection(index, 'down')} disabled={index === sections.length - 1}>
                                                <ArrowDownIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </Paper>
                    </Grid>

                    {/* Live Preview Panel */}
                    <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                        <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5', p: 2 }}>
                            <Box sx={{ flex: 1, bgcolor: 'white', boxShadow: 3, overflow: 'hidden' }}>
                                <iframe
                                    title="preview"
                                    srcDoc={getPreviewHTML()}
                                    style={{ width: '100%', height: '100%', border: 'none' }}
                                />
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </Box>
        </AnimatedPage>
    );
};

export default CustomTemplateBuilder;
